<script>
  import { createEventDispatcher, getContext, tick } from "svelte";
  import range from "lodash/range";
  import cloneDeep from "lodash/cloneDeep";
  import { Modal, clickOutside } from "svelte-utilities";

  import LocationItemIcon from "@local/assets/icons/location-item-filled.svg";
  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import CaretRightIcon from "@local/assets/icons/caret-right.svg";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import UppyDropzone from "#lib/dropzone/UppyDropzone.svelte";
  import SidebarSection from "./SidebarSection.svelte";

  import { showDocPages, selectedPages, hiddenPages } from "#src/stores/ui.js";
  import { api, getDocPdf } from "#src/api";
  import { SUPABASE_URL as supabaseUrl } from "#src/env";
  import { orderedList, addToSortableList } from "@local/extensions/collections/sortable-list.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import { isLocationVisible } from "#src/extensions/filter-items.js";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import eb from "#src/extensions/event-bus.js";
  import { isInFrameX } from "#lib/framex/derive.js";

  export let group;
  export let doc;
  export let docLocations;
  export let disabled;
  export let filters;
  export let hoveredPage = null;
  export let lastSelectedPage = null;
  export let isVirtual = false;
  // Opt-in only: the FrameX Pages tab marks rows in the FrameX working set
  // with a gold fill and strokes the selected row in the canvas's selection
  // blue, so membership and selection read as separate channels the way they
  // do on canvas. Defaults off; the legacy DocumentProperties caller keeps
  // its blue-fill/white-text selection.
  export let fxRowStyle = false;

  const dispatch = createEventDispatcher();
  const fileData = getContext("fileData");

  let importModal;
  let showHidden = false;
  let labelInput;
  let editingLabel = null;
  let labelValue = "";
  let showDocActions = false;
  let downloading = false;
  let blankPageCount = 0;

  $: pages = doc ? orderedList(doc.data.pages) : [];
  $: visiblePages = filterPages(pages, filters);
  $: visibleIndices = visiblePages.reduce((obj, pageId, index) => {
    obj[pageId] = index;
    return obj;
  }, {});
  $: hidden = pages.filter((p) => p.hidden);
  $: tempHiddenPages = Object.keys(filters?.hidden_pages || {});
  $: locations = docLocations[doc?.id] || [];
  $: pageLocations = getPageLocations(locations, filters);

  function numParser(v) {
    const result = parseFloat(v);
    if (isNaN(result)) throw new Error("Not parseable as a number");
    if (result <= 0) throw new Error("Value must be positive");
    return result;
  }

  function filterPages(pages, filters) {
    return pages
      .filter((p) => {
        if (p.hidden) return false;
        if (!filters?.hidden_pages) return true;
        if (filters.hidden_pages[p.id]) return false;
        return true;
      })
      .map((p) => p.id);
  }

  function getPageLocations(locations, filters) {
    return locations.reduce((obj, loc) => {
      const isVisible = isLocationVisible(loc, $group, filters);

      if (isVisible) {
        if (!obj[loc.page_id]) obj[loc.page_id] = [];
        obj[loc.page_id].push(loc);
      }

      return obj;
    }, {});
  }

  function selectPage(e, page) {
    if (e.shiftKey) {
      let newSelected;
      if (showHidden) {
        const prev = lastSelectedPage || doc.data.pages.order[0];
        const prevIndex = doc.data.pages.indices[prev];
        const currentIndex = doc.data.pages.indices[page.id];
        const min = Math.min(prevIndex, currentIndex);
        const max = Math.max(prevIndex, currentIndex);
        newSelected = range(min, max + 1).map((i) => doc.data.pages.order[i]);
      } else {
        const prev = lastSelectedPage || visiblePages[0];
        const prevIndex = visibleIndices[prev];
        const currentIndex = visibleIndices[page.id];
        const min = Math.min(prevIndex, currentIndex);
        const max = Math.max(prevIndex, currentIndex);
        newSelected = range(min, max + 1).map((i) => visiblePages[i]);
      }
      selectedPages.select(...newSelected);
      lastSelectedPage = page.id;
    } else if (discontiguousKey(e)) {
      selectedPages.toggle(page.id);
      lastSelectedPage = page.id;
    } else {
      selectedPages.selectOnly(page.id);
      lastSelectedPage = page.id;
    }
  }

  function handleKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      updateLabel(editingLabel, labelValue);
      editingLabel = null;
    } else if (e.key === "Escape") {
      editingLabel = null;
    }
  }

  function handleBlur() {
    if (!editingLabel) return;
    updateLabel(editingLabel, labelValue);
    editingLabel = null;
  }

  function beginAddingFile() {
    importModal.open();
  }

  async function beginEditingLabel(page) {
    if (disabled) return;
    editingLabel = page.id;
    labelValue = defaultLabel(page);
    await tick();
    labelInput.focus();
    labelInput.select();
  }

  function updateLabel(id, value) {
    if (value === defaultLabel(doc.data.pages[id])) {
      return;
    }

    const data = cloneDeep(doc.data);
    data.pages[id].label = value;
    group.updateDocument(doc.id, { data });
  }

  function beginRelabeling() {
    showDocActions = false;
    eb.dispatch("relabel-pages");
  }

  async function getPdf() {
    downloading = true;
    showDocActions = false;
    const pdf = await getDocPdf(doc.id);
    if (pdf.error) return;
    try {
      if (pdf.data.state !== "completed") {
        throw new Error(`PDF failure: ${pdf.data.state}, ${pdf.data.job_id}`);
      }
      const { data, error } = await api.storage.from("temp").download(pdf.data.path);
      if (error) throw error;
      downloadBlob(data, `${pdf.data.name}.pdf`);
    } catch (error) {
      console.log(error);
    }
    downloading = false;
  }

  function defaultLabel(page) {
    if (!page) return "";
    const sheet = $fileData[page.name]?.sheets[page.page];
    const index = doc.data.pages.indices[page.id];
    return page.label || sheet?.name || index + 1;
  }

  async function addFiles(e) {
    const { results } = e.detail;
    const imageTypes = ["image/jpeg", "image/png", "image/gif"];

    if (results.length) {
      const filenames = results.map(async (r) => {
        await api
          .schema("storage")
          .from("objects")
          .update({ user_metadata: { name: r.meta.name } })
          .eq("name", r.meta.objectName)
          .eq("bucket_id", "documents");
      });

      const ready = doc?.ready && results.every((f) => imageTypes.includes(f.meta.type)) ? true : false;

      const pages = [];

      results.forEach((r) => {
        const pending = imageTypes.includes(r.meta.type) ? false : true;
        pages.push({
          id: crypto.randomUUID(),
          name: r.meta.objectName,
          label: null,
          section: null,
          hidden: false,
          page: 0,
          pending,
          scale: null,
        });
      });

      const data = cloneDeep(doc.data);
      addToSortableList(data.pages, ...pages);
      group.updateDocument(doc.id, { data, ready });
      await Promise.all(filenames);
    }

    importModal.close();
  }

  function addBlankPages() {}

  function zoomToPage(id) {
    dispatch("zoom-to-page", { page: id });
  }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<SidebarSection>
  <div class="relative">
    <div class="flex justify-between items-center">
      <h3 class="mb-2 px-2">Pages</h3>
      <div class="flex gap-2 border border-transparent items-center">
        {#if !$showDocPages && pages.length}
          <div>
            ({pages.length})
          </div>
        {:else}
          {#if tempHiddenPages.length || hidden.length}
            <button on:click={() => (showHidden = !showHidden)} class="btn-text">
              {#if showHidden}
                Hide Invisible
              {:else}
                Show Invisible
              {/if}
            </button>
          {/if}
          <button
            class="relative p-1 rounded hover:bg-gray-200"
            use:clickOutside
            on:outclick={() => (showDocActions = false)}
            on:click={() => (showDocActions = !showDocActions)}>
            {#if downloading}
              <div class="animate-spin">
                <SpinnerIcon />
              </div>
            {:else}
              <EllipsisIcon />
            {/if}
            {#if showDocActions}
              <div class="dropdown-container right-0">
                <button class="dropdown-button-item" on:click|stopPropagation={getPdf} disabled={downloading}>
                  Export PDF
                </button>
                {#if !disabled}
                  <button class="dropdown-button-item" on:click|stopPropagation={beginRelabeling}>
                    Relabel Pages
                  </button>
                {/if}
              </div>
            {/if}
          </button>
        {/if}
      </div>
    </div>
    <button class="absolute top-0 p-1 left-0 -ml-3 -mt-1" on:click={() => ($showDocPages = !$showDocPages)}>
      {#if $showDocPages}
        <CaretDownIcon />
      {:else}
        <CaretRightIcon />
      {/if}
    </button>
  </div>
  {#if $showDocPages}
    <div>
      {#each pages as page, index}
        {@const file = $fileData[page.name]}
        {@const sheet = file?.sheets[page.page]}
        {@const label = page.label || sheet?.name || index + 1}
        {@const pageLocs = pageLocations[page.id]}
        {#if showHidden || (!page.hidden && !filters?.hidden_pages?.[page.id])}
          <div
            class="flex gap-2 py-1 px-4 select-none"
            class:items-center={fxRowStyle}
            class:fx-row={fxRowStyle}
            class:fx-selected={fxRowStyle && $selectedPages[page.id]}
            class:bg-blue-500={!fxRowStyle && $selectedPages[page.id]}
            class:text-white={!fxRowStyle && $selectedPages[page.id]}
            on:mouseover={() => (hoveredPage = page.id)}
            on:mouseout={() => (hoveredPage = null)}
            on:click|preventDefault={(e) => selectPage(e, page)}
            on:dblclick={() => zoomToPage(page.id)}>
            <div
              class="flex-none"
              class:w-4={!fxRowStyle}
              class:fx-num={fxRowStyle}
              class:fx-num-member={fxRowStyle && isInFrameX(page)}
              class:fx-num-selected={fxRowStyle && $selectedPages[page.id]}
              class:opacity-40={$hiddenPages[page.id]}
              class:opacity-30={page.hidden}>
              {index + 1}
            </div>
            {#if editingLabel === page.id}
              <input
                class="label-input grow"
                size="1"
                bind:this={labelInput}
                bind:value={labelValue}
                on:keydown={handleKeydown}
                on:blur={handleBlur} />
            {:else if $selectedPages[page.id] && Object.keys($selectedPages).length === 1}
              <div class="grow truncate">
                <button
                  class="truncate"
                  on:click|stopPropagation={() => beginEditingLabel(page)}
                  class:opacity-40={$hiddenPages[page.id]}
                  class:opacity-30={page.hidden}>
                  {label}
                </button>
              </div>
            {:else}
              <div
                class="text-left truncate grow"
                class:opacity-40={$hiddenPages[page.id]}
                class:opacity-30={page.hidden}>
                {label}
              </div>
            {/if}
            {#if pageLocs?.length}
              <div class="flex-none">
                <LocationItemIcon />
              </div>
            {/if}
          </div>
        {/if}
      {/each}
    </div>
    {#if !disabled}
      <div class="flex justify-end py-2">
        <button class="font-bold" on:click={beginAddingFile}> + Add Pages </button>
      </div>
    {/if}
  {/if}
</SidebarSection>

<Modal bind:this={importModal} closeable width="36rem">
  <div slot="title">Add Pages to Document</div>
  <div slot="content" class="space-y-4">
    <UppyDropzone
      uploadUrl={`${supabaseUrl}/storage/v1/upload/resumable`}
      bucket="documents"
      accept="application/pdf,image/jpeg,image/gif,image/png"
      height={350}
      on:complete={addFiles} />
    <!-- <div>
      <div class="flex items-center gap-2 text-xs justify-end">
        <div>Add blank pages:</div>
        <div class="w-24">
          <TextInput label="Quantity" bind:value={blankPageCount} parser={numParser} />
        </div>
        <button class="btn" on:click={addBlankPages}>Add Blank Pages</button>
      </div>
    </div> -->
  </div>
</Modal>

<style lang="scss">
  input.label-input {
    @apply grow bg-blue-200 text-black;
    border: none;
    outline: none;
    min-width: 0;
    max-width: 100%;
  }

  // Opt-in FrameX row treatment: neutral hover matching the Frames-tab marker
  // rows, and a light blue field when selected. Membership is the gold number
  // tile (`.fx-num-member` below), never a row fill — a gold row read as
  // "selected". Only reachable when a caller passes `fxRowStyle`.
  .fx-row {
    cursor: pointer;

    &:hover {
      background: #f7f8fa;
    }

    // Light blue field only — the blue border lives on the number tile, the
    // way the stepper's selected chip carries it. Declared after :hover so
    // selection wins on a hovered selected row.
    &.fx-selected {
      background: var(--fx-accent-tint, #eaf1fc);
    }
  }

  // The page number in the same tile as the stepper's "Pages in FrameX" chips
  // (framex/FrameXPanel.svelte's `.fx-chip`) so a page reads identically in
  // both places. Same literals as that rule — no new colours; the button
  // reset and cursor are omitted since this is a div, not a control. Present
  // on every row, not just FrameX members, so adding a page never changes row
  // height. Tokens carry fallbacks since this file is shared with the legacy
  // app, where framex.scss may not be loaded.
  .fx-num {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    padding: 0 7px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--fx-text-alt, #374151);
    background: #fff;
    border: 1px solid var(--fx-border, #e2e4e8);

    // Amber when the page is in the FrameX working set, matching the stepper
    // chips' membership colours (`.fx-chip` in framex/FrameXPanel.svelte) and the
    // app's own amber scale. Fallbacks because this file is shared with the legacy
    // app, where framex.scss may not be loaded.
    &.fx-num-member {
      color: var(--fx-amber-text, #92400e);
      background: var(--fx-amber-bg, #fef3c7);
      border-color: var(--fx-amber-border, #f3d98b);
    }

    // Selection beats membership. Same specificity as `.fx-num-member` above,
    // so source order is what decides — this must stay after it. Matches the
    // stepper's selected chip: blue border over the light blue field. `border`
    // rather than `border-color` because the weight changes too; the tile is
    // border-box, so it costs no size. The fill is the same token the row uses,
    // so on a selected row only the border shows — kept explicit anyway, so the
    // tile stays correct if the row colour ever changes.
    &.fx-num-selected {
      color: var(--fx-text-alt, #374151);
      background: var(--fx-accent-tint, #eaf1fc);
      border: 2px solid var(--fx-select-stroke, #0066e5);
    }
  }
</style>
